import { algebraicToSquare, parseFen } from "../game/board";
import { captureSquaresOf, legalMovesFrom } from "../game/moves";
import type { TutorialStep } from "../game/tutorialScript";
import { SceneManager } from "../render/scene";
import { PieceLayer } from "../render/pieceEntities";
import { MoveMarkers } from "../render/highlights";
import { TaverneuBubble } from "./taverneuBubble";

/**
 * Tutoriel scenarise et fixe : reutilise la vraie scene 3D (plateau, pieces,
 * eclairage) plutot qu'un rendu separe, chaque etape posant une position FEN
 * autonome (voir `TutorialStep`). Parametree par `steps` plutot que d'importer une
 * sequence fixe : sert aussi bien le tutoriel de regles que celui de strategies.
 * Navigation Precedent/Suivant, narration du Taverneu en bulle persistante (meme
 * composant qu'en jeu).
 */
export class TutorialController {
  private scene: SceneManager;
  private pieceLayer: PieceLayer;
  private markers: MoveMarkers;
  private bubble: TaverneuBubble;
  private hud: HTMLDivElement;
  private stepIndex = 0;

  constructor(
    uiRoot: HTMLElement,
    private readonly viewport: HTMLElement,
    private readonly steps: readonly TutorialStep[],
    private readonly onQuit: () => void,
  ) {
    this.scene = new SceneManager(viewport);
    this.scene.setBaseAzimuth("w");
    this.pieceLayer = new PieceLayer(this.scene.piecesGroup, this.scene.tweens);
    this.markers = new MoveMarkers(this.scene.piecesGroup);
    this.bubble = new TaverneuBubble(uiRoot);

    this.hud = document.createElement("div");
    this.hud.className = "tutorial-hud";
    this.hud.innerHTML = `
      <div class="tutorial-step-counter"></div>
      <div class="corner-buttons tutorial-quit">
        <button class="btn-corner" id="tutorial-quit-btn" type="button"><span class="ico">&times;</span><span class="lbl">Quitter</span></button>
      </div>
      <div class="action-bar tutorial-actions">
        <button class="btn btn-ghost" id="tutorial-prev-btn" type="button">Precedent</button>
        <button class="btn btn-primary" id="tutorial-next-btn" type="button">Suivant</button>
      </div>
    `;
    uiRoot.appendChild(this.hud);

    this.hud.querySelector("#tutorial-quit-btn")?.addEventListener("click", () => this.quit());
    this.hud.querySelector("#tutorial-prev-btn")?.addEventListener("click", () => this.go(-1));
    this.hud.querySelector("#tutorial-next-btn")?.addEventListener("click", () => this.go(1));

    this.render();
  }

  private go(delta: number): void {
    const next = this.stepIndex + delta;
    if (next < 0) return;
    if (next >= this.steps.length) {
      this.quit();
      return;
    }
    this.stepIndex = next;
    this.render();
  }

  private render(): void {
    const step = this.steps[this.stepIndex];
    if (!step) return;
    const state = parseFen(step.fen);
    this.pieceLayer.syncFromBoard(state.board);
    this.markers.hideAll();

    if (step.selectSquare) {
      const square = algebraicToSquare(step.selectSquare);
      this.markers.showSelection(square);
      const moves = legalMovesFrom(state, square);
      this.markers.showTargets(
        moves.map((m) => m.to),
        captureSquaresOf(moves),
      );
    } else if (step.targetSquares) {
      this.markers.showTargets(step.targetSquares.map(algebraicToSquare));
    }

    this.bubble.say({ text: step.narration }, { persistent: true });

    const counter = this.hud.querySelector<HTMLDivElement>(".tutorial-step-counter")!;
    counter.textContent = `Etape ${this.stepIndex + 1} / ${this.steps.length}`;

    const prevBtn = this.hud.querySelector<HTMLButtonElement>("#tutorial-prev-btn")!;
    prevBtn.disabled = this.stepIndex === 0;
    const nextBtn = this.hud.querySelector<HTMLButtonElement>("#tutorial-next-btn")!;
    nextBtn.textContent = this.stepIndex === this.steps.length - 1 ? "Termine" : "Suivant";
  }

  quit(): void {
    this.bubble.destroy();
    this.hud.remove();
    this.scene.dispose();
    this.viewport.querySelectorAll("canvas").forEach((c) => c.remove());
    this.onQuit();
  }
}
